import * as React from 'react';
import { useMemo } from 'react';

import { getLanguageMatch } from 'js/lib/language';

import { MenuList } from '@coursera/cds-core';

import type { CourseLanguages } from 'bundles/common/queries/useGetCourseLanguages';
import TranslationDropdownMenuItem from 'bundles/page-header/components/translation/TranslationDropdownMenuItem';
import type { Language } from 'bundles/page-header/components/translation/useCourseLanguageQuery';

import _t from 'i18n!nls/page';

type Props = {
  languages: Language[];
  currentLanguage: string;
  courseLanguages: CourseLanguages;
};

const TranslationDropdownMenuWithGrouping = ({ languages, currentLanguage, courseLanguages }: Props) => {
  const availableLanguages = useMemo(
    () =>
      languages.filter((language) => {
        const { autoTranslations, humanTranslations, originalLanguage } = courseLanguages;

        return (
          !!getLanguageMatch(language.key, originalLanguage) ||
          autoTranslations.find((translation) => !!getLanguageMatch(language.key, translation)) ||
          humanTranslations.find((translation) => !!getLanguageMatch(language.key, translation))
        );
      }),
    [courseLanguages, languages]
  );

  const subtitleLanguages = useMemo(
    () =>
      languages.filter((language) => {
        const { subtitles } = courseLanguages;

        return (
          subtitles.find((translation) => !!getLanguageMatch(language.key, translation)) &&
          !availableLanguages.find((availableLanguage) => availableLanguage.key === language.key)
        );
      }),
    [courseLanguages, languages, availableLanguages]
  );

  if (availableLanguages.length === 0) {
    return null;
  }

  return (
    <MenuList>
      <MenuList.Group label={_t('Available languages')}>
        {availableLanguages.map((language) => (
          <TranslationDropdownMenuItem key={language.key} language={language} currentLanguage={currentLanguage} />
        ))}
      </MenuList.Group>
      {subtitleLanguages.length > 0 && (
        <MenuList.Group label={_t('Limited translation')}>
          {subtitleLanguages.map((language) => (
            <TranslationDropdownMenuItem key={language.key} language={language} currentLanguage={currentLanguage} />
          ))}
        </MenuList.Group>
      )}
    </MenuList>
  );
};

export default TranslationDropdownMenuWithGrouping;
